import { getAISearchData } from 'selectors/searchNLP';
import { getIsCurrentSearchAI, getFilters } from 'selectors/search';

/** Keys that must not affect AI baseline equality (UI-only / transient). */
const TRANSIENT_FILTER_KEYS = new Set(['aiFilterSessionId', 'refreshSearch']);

function compareForSort(a, b) {
  return JSON.stringify(a).localeCompare(JSON.stringify(b));
}

/**
 * Deep canonicalise filter values for stable comparison: sort object keys,
 * sort arrays (by JSON string), trim strings.
 * @param {*} val
 * @returns {*}
 */
export function canonicalizeFilterValue(val) {
  if (val === null || val === undefined) {
    return val;
  }
  if (Array.isArray(val)) {
    return val.map(canonicalizeFilterValue).sort(compareForSort);
  }
  if (typeof val === 'object' && val.constructor === Object) {
    const out = {};
    for (const key of Object.keys(val).sort()) {
      out[key] = canonicalizeFilterValue(val[key]);
    }
    return out;
  }
  if (typeof val === 'string') {
    return val.trim();
  }
  return val;
}

/**
 * Stable fingerprint string for the search filters relevant to AI provenance.
 * @param {Record<string, unknown>} filters
 * @returns {string}
 */
export function buildAiFilterFingerprint(filters) {
  if (!filters || typeof filters !== 'object') {
    return '';
  }
  const stripped = { ...filters };
  for (const k of TRANSIENT_FILTER_KEYS) {
    delete stripped[k];
  }
  return JSON.stringify(canonicalizeFilterValue(stripped));
}

/**
 * True when current filters still match the AI-applied baseline stored on aiSearchData.
 * If no baseline was stored (legacy), returns true so we do not spuriously clear AI state.
 * @param {Record<string, unknown>} currentFilters
 * @param {object | null | undefined} aiSearchData
 * @returns {boolean}
 */
export function filtersMatchAIBaseline(currentFilters, aiSearchData) {
  if (!aiSearchData?.aiAppliedFingerprint) {
    return true;
  }
  return (
    buildAiFilterFingerprint(currentFilters) ===
    aiSearchData.aiAppliedFingerprint
  );
}

/**
 * True when the current results session is AI-generated for Save Search / sparkle:
 * session markers, NLP payload, and (when present) filter fingerprint match.
 */
export const isCurrentSearchAiGenerated = state => {
  const ai = getAISearchData(state);
  if (!getIsCurrentSearchAI(state) || !ai?.sessionId || !ai?.filterResponse) {
    return false;
  }
  if (ai.aiAppliedFingerprint) {
    return filtersMatchAIBaseline(getFilters(state), ai);
  }
  return true;
};
